import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PRODUCTS, GIFT_OCCASIONS, GIFT_EXTRAS, WHATSAPP_LINK } from "../../lib/constants";
import { Gift, Heart, Calendar, MessageSquare, Check, ChevronRight, X } from "lucide-react";
import { toast } from "sonner";
import confetti from "canvas-confetti";

const GiftSection = ({ onAddToCart }) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedOccasion, setSelectedOccasion] = useState("");
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [recipientName, setRecipientName] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [recipientAddress, setRecipientAddress] = useState("");
  const [message, setMessage] = useState("");
  const [senderName, setSenderName] = useState("");
  const [senderPhone, setSenderPhone] = useState("");
  const [showPreview, setShowPreview] = useState(false);

  const formatPrice = (price) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const getTotal = () => {
    if (!selectedProduct) return 0;
    const extrasTotal = selectedExtras.reduce((sum, extra) => sum + extra.price, 0);
    return selectedProduct.basePrice + extrasTotal;
  };

  const toggleExtra = (extra) => {
    setSelectedExtras((prev) =>
      prev.find((e) => e.id === extra.id)
        ? prev.filter((e) => e.id !== extra.id)
        : [...prev, extra]
    );
  };

  const handleSubmit = () => {
    if (!selectedProduct || !recipientName || !recipientPhone || !senderName) {
      toast.error("Por favor completa todos los campos requeridos");
      return;
    }

    // Generate WhatsApp message
    const extrasText = selectedExtras.length > 0 
      ? `\nExtras: ${selectedExtras.map(e => e.name).join(", ")}`
      : "";

    const whatsappMessage = encodeURIComponent(
      `¡Hola Urban Brownies! 🎁\n\n` +
      `Quiero enviar un REGALO:\n\n` +
      `🍫 Producto: ${selectedProduct.name}\n` +
      `🎉 Ocasión: ${selectedOccasion || "No especificada"}\n` +
      extrasText +
      `\n\n📍 DATOS DE ENTREGA:\n` +
      `👤 Para: ${recipientName}\n` +
      `📱 Teléfono: ${recipientPhone}\n` +
      `🏠 Dirección: ${recipientAddress || "Por definir"}\n` +
      `\n💌 MENSAJE DEDICATORIA:\n"${message || "Sin mensaje"}"\n` +
      `\n📞 Remitente: ${senderName} (${senderPhone})\n` +
      `\n💰 Total: ${formatPrice(getTotal())}`
    );

    window.open(`${WHATSAPP_LINK}?text=${whatsappMessage}`, "_blank");

    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ["#C88037", "#E5984A", "#D9381E"],
    });

    toast.success("¡Abriendo WhatsApp para confirmar tu regalo!");
  };

  // Filter products suitable for gifting
  const giftProducts = PRODUCTS.filter((p) => 
    ["urban-bow", "urban-box-6", "urban-decorated", "urban-caramel"].includes(p.id)
  );

  return (
    <section
      id="regala"
      className="py-20 md:py-32 bg-gradient-to-b from-[#FDFBF7] to-[#F4EDE4]"
      data-testid="gift-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-[#C88037] font-medium text-sm uppercase tracking-wider flex items-center justify-center gap-2">
            <Heart className="w-4 h-4" strokeWidth={1.5} />
            Sorprende a Alguien
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#2D1810] mt-2 mb-4">
            <span className="italic text-[#C88037]">Regala</span> un momento dulce
          </h2>
          <p className="text-[#5E4B3C] max-w-2xl mx-auto">
            ¿Quieres alegrarle el día a esa persona especial? Envía brownies con un mensaje 
            personalizado directo a su puerta. Nosotros cuidamos cada detalle de la sorpresa.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left: Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            {/* Step 1: Select Product */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-serif text-xl text-[#2D1810] mb-4 flex items-center gap-2">
                <Gift className="w-5 h-5 text-[#C88037]" strokeWidth={1.5} />
                1. Elige el regalo
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {giftProducts.map((product) => (
                  <button
                    key={product.id}
                    onClick={() => setSelectedProduct(product)}
                    className={`relative rounded-xl overflow-hidden border-2 transition-all ${
                      selectedProduct?.id === product.id
                        ? "border-[#C88037] ring-2 ring-[#C88037]/20"
                        : "border-transparent hover:border-[#E5DCD0]"
                    }`}
                    data-testid={`gift-product-${product.id}`}
                  >
                    <div className="aspect-square">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-[#2D1810]/80 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                      <p className="font-semibold text-sm">{product.name}</p>
                      <p className="text-xs text-white/80">{product.priceRange}</p>
                    </div>
                    {selectedProduct?.id === product.id && (
                      <div className="absolute top-2 right-2 bg-[#C88037] text-white p-1 rounded-full">
                        <Check className="w-4 h-4" strokeWidth={2} />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Step 2: Occasion */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-serif text-xl text-[#2D1810] mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#C88037]" strokeWidth={1.5} />
                2. ¿Cuál es la ocasión?
              </h3>
              <div className="flex flex-wrap gap-2">
                {GIFT_OCCASIONS.map((occasion) => (
                  <button
                    key={occasion}
                    onClick={() => setSelectedOccasion(occasion)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      selectedOccasion === occasion
                        ? "bg-[#C88037] text-white"
                        : "bg-[#F4EDE4] text-[#5E4B3C] hover:bg-[#E5DCD0]"
                    }`}
                    data-testid={`occasion-${occasion}`}
                  >
                    {occasion}
                  </button>
                ))}
              </div>
            </div>

            {/* Step 3: Extras */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-serif text-xl text-[#2D1810] mb-4">
                3. Agrega un detalle extra
              </h3>
              <div className="space-y-3">
                {GIFT_EXTRAS.map((extra) => (
                  <button
                    key={extra.id}
                    onClick={() => toggleExtra(extra)}
                    className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                      selectedExtras.find((e) => e.id === extra.id)
                        ? "border-[#C88037] bg-[#C88037]/5"
                        : "border-[#E5DCD0] hover:border-[#C88037]/50"
                    }`}
                    data-testid={`extra-${extra.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          selectedExtras.find((e) => e.id === extra.id)
                            ? "border-[#C88037] bg-[#C88037]"
                            : "border-[#E5DCD0]"
                        }`}
                      >
                        {selectedExtras.find((e) => e.id === extra.id) && (
                          <Check className="w-3 h-3 text-white" strokeWidth={3} />
                        )}
                      </div>
                      <span className="text-[#2D1810] font-medium">{extra.name}</span>
                    </div>
                    <span className="text-[#C88037] font-semibold">
                      +{formatPrice(extra.price)}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Step 4: Recipient Info */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-serif text-xl text-[#2D1810] mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5 text-[#C88037]" strokeWidth={1.5} />
                4. ¿A quién sorprendemos?
              </h3>
              <div className="space-y-4">
                <input
                  type="text"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  placeholder="Nombre de quien recibirá el regalo *"
                  className="w-full px-4 py-3 rounded-xl border border-[#E5DCD0] focus:border-[#C88037] focus:ring-1 focus:ring-[#C88037] outline-none"
                  data-testid="gift-recipient-name"
                />
                <input
                  type="tel"
                  value={recipientPhone}
                  onChange={(e) => setRecipientPhone(e.target.value)}
                  placeholder="Teléfono del destinatario *"
                  className="w-full px-4 py-3 rounded-xl border border-[#E5DCD0] focus:border-[#C88037] focus:ring-1 focus:ring-[#C88037] outline-none"
                  data-testid="gift-recipient-phone"
                />
                <input
                  type="text"
                  value={recipientAddress}
                  onChange={(e) => setRecipientAddress(e.target.value)}
                  placeholder="Dirección de entrega (Bucaramanga)"
                  className="w-full px-4 py-3 rounded-xl border border-[#E5DCD0] focus:border-[#C88037] focus:ring-1 focus:ring-[#C88037] outline-none"
                  data-testid="gift-recipient-address"
                />
              </div>
            </div>

            {/* Step 5: Message */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-serif text-xl text-[#2D1810] mb-4 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-[#C88037]" strokeWidth={1.5} />
                5. Tu dedicatoria
              </h3>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Escribe aquí tu mensaje especial... (máx. 150 caracteres)"
                maxLength={150}
                rows={3}
                className="w-full px-4 py-3 rounded-xl border border-[#E5DCD0] focus:border-[#C88037] focus:ring-1 focus:ring-[#C88037] outline-none resize-none"
                data-testid="gift-message"
              />
              <div className="text-right text-sm text-[#5E4B3C] mt-1">
                {message.length}/150
              </div>
              
              {/* Message suggestions */}
              <div className="mt-3">
                <p className="text-xs text-[#5E4B3C] mb-2">Ideas de mensajes:</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    "¡Te quiero mucho!",
                    "Gracias por existir",
                    "Disfruta este momento dulce",
                  ].map((suggestion) => (
                    <button
                      key={suggestion}
                      onClick={() => setMessage(suggestion)}
                      className="text-xs bg-[#F4EDE4] text-[#5E4B3C] px-3 py-1 rounded-full hover:bg-[#E5DCD0] transition-colors"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Step 6: Sender Info */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-serif text-xl text-[#2D1810] mb-4">
                6. Tus datos (remitente)
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <input
                  type="text"
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  placeholder="Tu nombre *"
                  className="w-full px-4 py-3 rounded-xl border border-[#E5DCD0] focus:border-[#C88037] focus:ring-1 focus:ring-[#C88037] outline-none"
                  data-testid="gift-sender-name"
                />
                <input
                  type="tel"
                  value={senderPhone}
                  onChange={(e) => setSenderPhone(e.target.value)}
                  placeholder="Tu teléfono *"
                  className="w-full px-4 py-3 rounded-xl border border-[#E5DCD0] focus:border-[#C88037] focus:ring-1 focus:ring-[#C88037] outline-none"
                  data-testid="gift-sender-phone"
                />
              </div>
            </div>
          </motion.div>

          {/* Right: Preview */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:sticky lg:top-24"
          >
            <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-[#C88037]/20">
              <h3 className="font-serif text-xl text-[#2D1810] mb-4 flex items-center gap-2">
                <Gift className="w-5 h-5 text-[#C88037]" strokeWidth={1.5} />
                Vista Previa del Regalo
              </h3>

              {/* Gift Preview Card */}
              <div className="gift-preview rounded-xl p-6 mb-6 text-center">
                {selectedProduct ? (
                  <>
                    <img
                      src={selectedProduct.image}
                      alt={selectedProduct.name}
                      className="w-32 h-32 object-cover rounded-xl mx-auto mb-4 shadow-md"
                    />
                    <h4 className="font-serif text-lg text-[#2D1810]">
                      {selectedProduct.name}
                    </h4>
                    {selectedOccasion && (
                      <span className="inline-block bg-[#C88037]/10 text-[#C88037] text-sm px-3 py-1 rounded-full mt-2">
                        {selectedOccasion}
                      </span>
                    )}
                    {message && (
                      <p className="mt-4 italic text-[#5E4B3C] border-t border-[#E5DCD0] pt-4">
                        "{message}"
                      </p>
                    )}
                    {recipientName && (
                      <p className="mt-2 text-[#2D1810] font-medium">
                        Para: {recipientName}
                      </p>
                    )}
                    {senderName && (
                      <p className="text-sm text-[#5E4B3C]">
                        De: {senderName}
                      </p>
                    )}
                  </>
                ) : (
                  <div className="py-8 text-[#5E4B3C]">
                    <Gift className="w-12 h-12 mx-auto mb-4 opacity-30" strokeWidth={1.5} />
                    <p>Selecciona un producto para ver la vista previa</p>
                  </div>
                )}
              </div>

              {/* Order Summary */}
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-[#5E4B3C]">
                  <span>Producto:</span>
                  <span>{selectedProduct ? formatPrice(selectedProduct.basePrice) : "-"}</span>
                </div>
                {selectedExtras.length > 0 && (
                  <div className="flex justify-between text-[#5E4B3C]">
                    <span>Extras:</span>
                    <span>
                      {formatPrice(selectedExtras.reduce((sum, e) => sum + e.price, 0))}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-[#2D1810] font-bold text-lg pt-3 border-t border-[#E5DCD0]">
                  <span>Total:</span>
                  <span className="text-[#C88037]">{formatPrice(getTotal())}</span>
                </div>
              </div>

              {/* Submit Button */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSubmit}
                disabled={!selectedProduct}
                className={`w-full py-4 rounded-full font-semibold flex items-center justify-center gap-2 transition-colors ${
                  selectedProduct
                    ? "bg-[#25D366] text-white hover:bg-[#20BD5A]"
                    : "bg-[#E5DCD0] text-[#5E4B3C] cursor-not-allowed"
                }`}
                data-testid="send-gift-whatsapp"
              >
                <Heart className="w-5 h-5" strokeWidth={1.5} />
                Enviar Regalo por WhatsApp
              </motion.button>

              <p className="text-center text-xs text-[#5E4B3C] mt-4">
                Coordinaremos la entrega sorpresa por WhatsApp
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default GiftSection;
